import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import {
  ArrowLeft,
  Globe,
  Bell,
  User,
  Briefcase,
  Wrench,
  Loader2
} from 'lucide-react';

const languages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'pt', name: 'Português' },
  { code: 'fr', name: 'Français' }
];

export default function Settings() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, updateUserRole } = useAuth();

  const [notifications, setNotifications] = useState(true);
  const [roleLoading, setRoleLoading] = useState(false);

  const handleLanguageChange = (code) => {
    i18n.changeLanguage(code);
    toast.success(t('common.success'));
  };

  const handleRoleChange = async (newRole) => {
    if (newRole === user?.role) return;

    setRoleLoading(true);
    try {
      await updateUserRole(newRole);
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setRoleLoading(false);
    }
  };

  const handleNotificationToggle = async (enabled) => {
    setNotifications(enabled);
    
    if (enabled && 'Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Notification permission denied');
        setNotifications(false);
      } else {
        toast.success('Notifications enabled');
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24 md:pb-8">
      <Navbar />

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{t('common.back')}</span>
        </button>

        <h1 className="text-2xl font-bold text-slate-900 mb-6">{t('settings.title')}</h1>

        <div className="space-y-6">
          {/* Language */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                <Globe className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">{t('settings.language')}</h2>
                <p className="text-sm text-slate-500">Select your preferred language</p>
              </div>
            </div>

            <Select value={i18n.language} onValueChange={handleLanguageChange}>
              <SelectTrigger data-testid="language-select" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notifications */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                  <Bell className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-900">{t('settings.notifications')}</h2>
                  <p className="text-sm text-slate-500">{t('settings.enable_notifications')}</p>
                </div>
              </div>
              <Switch
                data-testid="notifications-switch"
                checked={notifications}
                onCheckedChange={handleNotificationToggle}
              />
            </div>
          </div>

          {/* Role */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                <User className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">{t('settings.change_role')}</h2>
                <p className="text-sm text-slate-500">
                  {t('settings.current_role')}: <span className="font-medium capitalize">{user?.role}</span>
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <button
                data-testid="role-worker-btn"
                onClick={() => handleRoleChange('worker')}
                disabled={roleLoading}
                className={`flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                  user?.role === 'worker'
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <Wrench className={`w-8 h-8 ${user?.role === 'worker' ? 'text-emerald-600' : 'text-slate-500'}`} />
                <span className={`font-medium ${user?.role === 'worker' ? 'text-emerald-700' : 'text-slate-700'}`}>
                  {t('settings.worker')}
                </span>
              </button>

              <button
                data-testid="role-employer-btn"
                onClick={() => handleRoleChange('employer')}
                disabled={roleLoading}
                className={`flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                  user?.role === 'employer'
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                {roleLoading ? (
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                ) : (
                  <Briefcase className={`w-8 h-8 ${user?.role === 'employer' ? 'text-emerald-600' : 'text-slate-500'}`} />
                )}
                <span className={`font-medium ${user?.role === 'employer' ? 'text-emerald-700' : 'text-slate-700'}`}>
                  {t('settings.employer')}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
